<template>
    <div class="relative w-full">
        <input
            v-model="query"
            type="text"
            class="w-full rounded-lg bg-nomad-800 border border-nomad-700 text-white placeholder:text-nomad-400 focus:border-nomad-accent focus:ring-nomad-accent disabled:cursor-not-allowed disabled:opacity-60"
            :placeholder="placeholder"
            :disabled="selected_contents.length >= Number(max)"
            autocomplete="off"
        />

        <ul
            v-if="matchingContents.length"
            class="absolute inset-x-0 top-full z-20 mt-2 max-h-72 overflow-y-auto rounded-lg bg-nomad-800 p-2 shadow-xl"
        >
            <li v-for="content in matchingContents" :key="content.id">
                <button
                    type="button"
                    class="flex w-full items-center gap-3 rounded-md p-2 text-left text-white transition-colors hover:bg-nomad-700"
                    @click="selectContent(content)"
                >
                    <img
                        :src="imageFor(content)"
                        :alt="`${content.name} image`"
                        class="h-11 w-11 shrink-0 rounded-md object-cover"
                    />
                    <span class="min-w-0 truncate">{{ content.name }}</span>
                </button>
            </li>
        </ul>

        <ul v-if="selected_contents.length" class="mt-3 grid gap-2 sm:grid-cols-2">
            <li
                v-for="content in selected_contents"
                :key="content.id"
                class="flex min-w-0 items-center gap-3 rounded-lg bg-nomad-800 p-2"
            >
                <img
                    :src="imageFor(content)"
                    :alt="`${content.name} image`"
                    class="h-12 w-12 shrink-0 rounded-md object-cover"
                />
                <span class="min-w-0 flex-1 truncate text-sm font-medium text-nomad-200">{{ content.name }}</span>
                <button
                    type="button"
                    class="shrink-0 rounded-md p-2 text-nomad-300 transition-colors hover:bg-nomad-700 hover:text-white"
                    :aria-label="`Remove ${content.name}`"
                    @click="removeContent(content.id)"
                >
                    <span class="icon-x" aria-hidden="true"></span>
                </button>
            </li>
        </ul>
    </div>
</template>

<script>
import { debounce, remove } from '@/utils/helpers';
import axios from 'axios';

export default {
    props: {
        type: {
            type: String,
            default: 'artists',
        },
        placeholder: {
            type: String,
            default: 'select artist',
        },
        max: {
            type: [String, Number],
            default: 1,
        },
        modelValue: {
            type: [Array, Object],
            default: () => [],
        },
    },
    emits: ['update:modelValue'],
    data() {
        return {
            contentType: this.type,
            query: null,
            contents: {},
            selected_contents: Array.isArray(this.modelValue)
                ? [...this.modelValue]
                : (this.modelValue ? [this.modelValue] : []),
        };
    },
    computed: {
        matchingContents() {
            const results = this.contents[this.contentType];
            return Array.isArray(results) ? results : [];
        },
    },
    methods: {
        getContent() {
            if (!this.query) {
                this.contents = {};
                return;
            }

            // Songs only ever go in platform playlists, never people's own
            if (this.contentType === 'playlists') {
                axios
                    .get('/api/backstage/playlist-search', { params: { q: this.query } })
                    .then((response) => {
                        this.contents = { playlists: response.data };
                    });
                return;
            }

            axios
                .get('/api/search', { params: { q: this.query } })
                .then((response) => {
                    this.contents = response.data;
                });
        },
        imageFor(content) {
            const image = content.image || content.cover || content.cover_image;
            if (image) {
                return image;
            }

            return 'https://open.nomadlyrics.com/assets/images/no-artist.png';
        },
        selectContent(content) {
            if (
                this.selected_contents.length >= Number(this.max)
                || this.selected_contents.some((selected) => selected.id === content.id)
            ) {
                return;
            }

            this.selected_contents.push(content);
            this.contents = {};
            this.$emit('update:modelValue', [...this.selected_contents]);
            this.query = null;
        },
        removeContent(id) {
            remove(this.selected_contents, (content) => content.id === id);
            this.$emit('update:modelValue', [...this.selected_contents]);
        },
    },
    watch: {
        modelValue: {
            deep: true,
            handler(value) {
                this.selected_contents = Array.isArray(value)
                    ? [...value]
                    : (value ? [value] : []);
            },
        },
        query: debounce(function () {
            this.getContent();
        }, 300),
    },
};
</script>
